// Card cover images — designed covers themed to each client's industry.
import coverVolvoGroup from "../assets/images/cases/volvo-group.jpg";
import coverIkea from "../assets/images/cases/ikea-it.jpg";
import coverVolvoCars from "../assets/images/cases/volvo-cars.jpg";
import coverLansforsakringar from "../assets/images/cases/lansforsakringar.jpg";

export type CaseStudy = {
  slug: string;
  client: string;
  title: string;
  status: "ongoing" | "delivered";
  tags: string[];
  summary: string;
  body: string[];
  image: string;
};

// Track record, sourced from the IDP delivery partner brief.
export const caseStudies: CaseStudy[] = [
  {
    slug: "volvo-group",
    client: "Volvo Group",
    title: "The platform the teams made their own",
    status: "ongoing",
    image: coverVolvoGroup,
    tags: ["Backstage", "IDP", "Platform Engineering"],
    summary:
      "Hundreds of development teams, each in their own bubble. Starting a new project took weeks. We turned the platform into something teams own.",
    body: [
      "Hundreds of development teams spread across a global organisation, each working in their own bubble, own tools, own processes, no common standard. Starting a new project took weeks.",
      "We joined the growing Backstage team as a tech lead and three developers. We came not just to write code, but to understand. Working side by side with Volvo Group's own teams, we identified what was genuinely slowing developers down and built solutions that solved real problems.",
      "When adoption began to move, it accelerated. Templates that automated what previously took weeks. A live API catalogue connected to source systems. TechDocs that stay current. AI integration that helps developers find answers without getting lost in the wrong Slack channel.",
      "The strongest proof: 70% of all templates are today contributed by teams outside the Backstage team. The platform had stopped being our project. It had become theirs.",
    ],
  },
  {
    slug: "ikea-it",
    client: "IKEA IT",
    title: "Backstage at scale, from day one",
    status: "ongoing",
    image: coverIkea,
    tags: ["Backstage", "Self-service", "Catalog", "Health checks"],
    summary:
      "Part of IKEA's Backstage and IDP journey from day one — discovery, core team, and the contribution model that lets hundreds of developers build on the platform.",
    body: [
      "We have been part of IKEA's Backstage and IDP journey from day one, covering the discovery phase, establishing the core team, and developing and evolving the platform over time. Also including establishing the delivery and contribution model, and the inner source practices that allow hundreds of developers and platform teams across a large, complex organisation to contribute to and benefit from the platform.",
      "Self-service capabilities, catalog adoption and health checks give teams actionable insight for compliance and a shorter path to production.",
      "From this initiative we have learnt a lot about driving platform adoption at scale, and that it's as much an organisational and leadership challenge as a technical one.",
    ],
  },
  {
    slug: "volvo-cars",
    client: "Volvo Cars",
    title: "Commit to cloud, zero manual steps",
    status: "ongoing",
    image: coverVolvoCars,
    tags: ["Backstage", "GitOps", "Observability", "OpenShift"],
    summary:
      "Specialist expertise across two core planes of a new Developer Experience organisation — commit-to-cloud with zero manual steps, and onboarding from days to seconds.",
    body: [
      "Volvo Cars built their Developer Experience organisation with the ambition of changing how their developers work. We contributed specialist expertise in two of the platform's core areas.",
      "Developer & Control Plane: a senior developer embedded in the team, responsible for Backstage implementation and GitOps-driven infrastructure provisioning. Commit to cloud — zero manual steps, full compliance and traceability. Technologies: Azure, Terraform, GitHub Actions, Kubernetes, OpenShift.",
      "Observability Plane: two DevOps engineers built a centralised observability stack with Grafana, Elasticsearch and Prometheus on Kubernetes. Onboarding a new team went from days to seconds.",
    ],
  },
  {
    slug: "lansforsakringar",
    client: "Länsförsäkringar",
    title: "Security and compliance, by default",
    status: "ongoing",
    image: coverLansforsakringar,
    tags: ["Red Hat Developer Hub", "OpenShift", "Compliance"],
    summary:
      "An IDP on top of existing platform services, with efficiency, security and compliance by default. We hold key roles as tech lead and architect.",
    body: [
      "Länsförsäkringar is building an IDP on top of their existing platform services, with focus on efficiency, security and compliance by default. By introducing self-service capabilities and golden paths, the path to production becomes shorter, simpler and less error prone, freeing up teams to focus on customer value and innovation.",
      "We hold key roles in the established IDP team, as tech lead and architect.",
    ],
  },
];
